import ErrorAlert from "@/components/ErrorAlert";
import { useTranslation } from "@/i18n";
import { PrecheckResponse, useApiActions } from "@/lib/actions";
import React, { useEffect, useState } from "react";
import CompletedStep from "./-components/CompletedStep";
import DocumentReview from "./-components/DocumentReview";
import DocumentSelection from "./-components/DocumentSelection";
import ErrorStep from "./-components/ErrorStep";
import LoadingStep from "./-components/LoadingStep";
import ProcessSidebar from "./-components/ProcessSidebar";

/**
 * Represents the current step in the document upload workflow.
 */
type Step =
  | "selection"
  | "analyzing"
  | "review"
  | "processing"
  | "completed"
  | "error";

/**
 * Main document upload workflow component.
 * Manages a multi-step process: file selection → AI analysis → review → CSV generation → completion.
 *
 * Workflow:
 * 1. **Selection**: User selects document (PDF/DOCX) and AI provider
 * 2. **Analyzing**: AI extracts process information from document
 * 3. **Review**: User reviews extracted data and can re-run analysis
 * 4. **Processing**: Converts JSON to CSV and uploads to Azure/aiio
 * 5. **Completed**: Shows success message and offers automation download
 *
 * @returns Document upload workflow UI
 */
/**
 * Time savings data structure
 */
interface TimeSavings {
  totalModelingTimeMinutes: number;
  totalReadingTimeMinutes: number;
  totalWorkingDaysSaved: number;
}

export function DocumentUpload() {
  const { t } = useTranslation();
  const { extractDocument, convertJsonRaw, precheck } = useApiActions();
  const [currentStep, setCurrentStep] = useState<Step>("selection");
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [streaming, setStreaming] = useState<boolean>(false);
  const [importErrorDetails, setImportErrorDetails] = useState<string | null>(
    null
  );
  const [timeSavings, setTimeSavings] = useState<TimeSavings | null>(null);

  // Precheck state
  const [precheckResult, setPrecheckResult] = useState<PrecheckResponse | null>(
    null
  );
  const [precheckLoading, setPrecheckLoading] = useState<boolean>(false);

  // Maximum file size: 2 MB
  const MAX_FILE_SIZE = 2 * 1024 * 1024;

  /**
   * Runs precheck validation on component mount.
   * Validates user permissions before allowing Forge operations.
   */
  useEffect(() => {
    const runPrecheck = async () => {
      setPrecheckLoading(true);
      try {
        const result = await precheck();
        setPrecheckResult(result);
      } catch (e: unknown) {
        // On error, set an invalid precheck result
        setPrecheckResult({
          valid: false,
          message: e instanceof Error ? e.message : "Precheck failed",
          checks: {},
          missing_requirements: [
            t("documentUpload.selection.errorInsufficientPermissions"),
          ],
        });
      } finally {
        setPrecheckLoading(false);
      }
    };

    runPrecheck();
  }, []);

  /**
   * Handles file selection with size validation.
   * Rejects files larger than 2 MB and shows appropriate error message.
   *
   * @param selectedFile - The file to validate and set
   */
  const handleFileChange = (selectedFile: File | null) => {
    if (!selectedFile) {
      setFile(null);
      setError(null);
      return;
    }

    // Validate file size
    if (selectedFile.size > MAX_FILE_SIZE) {
      const sizeMB = (selectedFile.size / (1024 * 1024)).toFixed(2);
      setError(t("documentUpload.selection.fileTooLarge", { size: sizeMB }));
      setFile(null);
      return;
    }

    // File is valid
    setFile(selectedFile);
    setError(null);
  };

  /**
   * Resets the workflow to the initial selection step.
   * Clears all state including file, extracted data, and messages.
   */
  const resetToStart = () => {
    setCurrentStep("selection");
    setFile(null);
    setError(null);
    setNotice(null);
    setStreaming(false);
    setImportErrorDetails(null);
    setTimeSavings(null);
    // Clear localStorage
    localStorage.removeItem("aiio_extracted_data");
    localStorage.removeItem("aiio_dropdown_data");
  };

  /**
   * Runs document analysis with AI extraction.
   * Sends document to backend, stores extracted data, and moves to review step.
   * On error, returns to the fallback step (selection for initial submit, review for rerun).
   *
   * @param fallbackStep - Step to return to on error
   */
  const runAnalysis = async (fallbackStep: Step) => {
    if (!file) return;
    setError(null);
    setNotice(null);
    setCurrentStep("analyzing");

    try {
      const form = new FormData();
      form.append("document", file);
      form.append(
        "ai_provider",
        localStorage.getItem("aiio_ai_provider") || "anthropic"
      );

      const result = await extractDocument(form);
      if (!result.ok) {
        throw new Error(result.error || t("documentUpload.error.message"));
      }

      // Store extracted data and AI provider for later use
      if (result.data) {
        localStorage.setItem(
          "aiio_extracted_data",
          JSON.stringify(result.data)
        );
        localStorage.setItem(
          "aiio_ai_provider",
          localStorage.getItem("aiio_ai_provider") || "anthropic"
        );
      }

      // Store dropdown data separately if available
      if ((result as any).dropdown_data) {
        localStorage.setItem(
          "aiio_dropdown_data",
          JSON.stringify((result as any).dropdown_data)
        );
      }

      if (result.saved_path)
        setNotice(`JSON debug saved at: ${result.saved_path}`);

      // Move to review step
      setCurrentStep("review");
    } catch (e: unknown) {
      const errorMessage = e instanceof Error ? e.message : "Unknown error";
      setError(errorMessage);
      setCurrentStep(fallbackStep);
    }
  };

  /**
   * Handles initial document submission.
   * Validates file presence before running analysis.
   *
   * @param e - Form submission event
   */
  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file) {
      setError(t("documentUpload.selection.selectPdfOrDocx"));
      return;
    }
    await runAnalysis("selection");
  };

  /**
   * Re-runs document analysis with current settings.
   * Useful when user wants to retry AI extraction with different parameters.
   */
  const rerun = async () => {
    await runAnalysis("review");
  };

  /**
   * Starts CSV generation and aiio upload.
   * Converts extracted JSON to CSV and uploads the data into aiio.
   */
  const proceed = async () => {
    // Read extracted data from localStorage
    const extractedDataStr = localStorage.getItem("aiio_extracted_data");
    if (!extractedDataStr) return;

    const extracted = JSON.parse(extractedDataStr);
    setError(null);
    setNotice(null);
    setImportErrorDetails(null);
    setCurrentStep("processing");
    setStreaming(true);

    try {
      // Send JSON data to converter
      const res = await convertJsonRaw(extracted, "extracted");

      // Check if response is ok
      if (!res.ok) {
        throw new Error(`Conversion failed: ${res.status} ${res.statusText}`);
      }

      // Wait for the streaming response to complete and parse for errors
      const reader = res.body?.getReader();
      if (reader) {
        const decoder = new TextDecoder();
        let streamedText = "";

        // Read the stream until complete
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          const chunk = decoder.decode(value, { stream: true });
          streamedText += chunk;
        }

        // Check if the stream contains error indicators
        const hasImportError = streamedText.includes("✗ Import failed:");

        if (hasImportError) {
          // Extract error details from the stream
          const errorStartIndex = streamedText.indexOf("✗ Import failed:");
          const errorEndIndex = streamedText.indexOf("✓ Conversion complete");
          const errorSection = streamedText
            .substring(
              errorStartIndex,
              errorEndIndex >= 0 ? errorEndIndex : streamedText.length
            )
            .trim();

          setImportErrorDetails(errorSection);
          setCurrentStep("error");
          return;
        }
        // Parse the final streamed response to extract time savings
        try {
          // Extract JSON from the streamed text (it may contain additional text like "Import response:" and "✓ Conversion complete")
          const jsonMatch = streamedText.match(/\{[\s\S]*\}/);
          if (jsonMatch) {
            const importResponse = JSON.parse(jsonMatch[0]);
            if (importResponse.success && importResponse.response) {
              const responseData = JSON.parse(importResponse.response);
              if (responseData.time_savings) {
                setTimeSavings({
                  totalModelingTimeMinutes:
                    responseData.time_savings.total_modeling_time_minutes || 0,
                  totalReadingTimeMinutes:
                    responseData.time_savings.total_reading_time_minutes || 0,
                  totalWorkingDaysSaved:
                    responseData.time_savings.total_working_days_saved || 0,
                });
              }
            }
          }
        } catch (parseError) {
          console.error("Failed to parse import response:", parseError);
        }
      } else {
        // If no body/stream, just wait for response to complete
        await res.text();
      }

      // Only move to completed step after stream is fully consumed and no errors detected
      setCurrentStep("completed");
    } catch (e: unknown) {
      const errorMessage = e instanceof Error ? e.message : "Unknown error";
      setError(errorMessage);
      setCurrentStep("review");
    } finally {
      setStreaming(false);
    }
  };

  /**
   * Renders the appropriate step component based on current workflow state.
   *
   * @returns The current step's UI component
   */
  const renderCurrentStep = () => {
    switch (currentStep) {
      case "selection":
        return (
          <DocumentSelection
            file={file}
            busy={false}
            error={currentStep === "selection" ? error : null}
            onFileChange={handleFileChange}
            onSubmit={onSubmit}
            precheckResult={precheckResult}
            precheckLoading={precheckLoading}
          />
        );
      case "analyzing":
        return <LoadingStep currentStep="analyzing" />;
      case "review":
        return (
          <DocumentReview
            busy={false}
            rerunning={false}
            proceeding={false}
            streaming={streaming}
            onRerun={rerun}
            onProceed={proceed}
            onBack={resetToStart}
          />
        );
      case "processing":
        return <LoadingStep currentStep="processing" />;
      case "completed":
        return (
          <CompletedStep
            notice={notice}
            onRestart={resetToStart}
            onBackToReview={() => setCurrentStep("review")}
            timeSavings={timeSavings}
          />
        );
      case "error":
        return (
          <ErrorStep
            errorDetails={importErrorDetails || "Unknown error occurred"}
            onRestart={resetToStart}
            onBackToReview={() => setCurrentStep("review")}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="bg-[#fafafa] flex flex-col">
      {/* Main Container with exact Figma dimensions and padding */}
      <div className="w-[1440px] h-[1024px] mx-auto flex flex-col pt-[73px] pb-[73px] px-6">
        {/* Header */}
        <div className="flex flex-col items-end gap-8 w-full">
          {/* Main Content Row */}
          <div className="flex justify-start items-start w-full gap-32 pr-[92px] pl-[70px]">
            {/* Sidebar */}
            <div className="w-[158px] flex-shrink-0">
              <ProcessSidebar rerunning={false} currentStep={currentStep} />
            </div>

            {/* Main Content Area */}
            <div className="flex justify-start items-start flex-1">
              <div className="w-[852px] rounded-2xl flex flex-col justify-start items-start">
                {renderCurrentStep()}

                {currentStep !== "completed" &&
                  currentStep !== "selection" &&
                  error && <ErrorAlert error={error} className="mt-4" />}
                {currentStep !== "completed" && notice && (
                  <p className="text-green-700 mt-4">{notice}</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
